import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputClasses =
  'mt-2 bg-[#050814] border-[#B8860B]/30 text-[#F4F1EA] placeholder:text-[#F4F1EA]/40 focus-visible:ring-[#B8860B]';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/contact/submit`, formData);
      toast({
        title: 'Message sent!',
        description: response.data.message || "We'll get back to you within 24 hours.",
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: 'Failed to send message',
        description: error.response?.data?.detail || 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Location',
      content: ['2724 Private Road 8160', 'Lovelady, TX 75851'],
    },
    {
      icon: Phone,
      title: 'Phone',
      content: ['(832) 714-0101', 'Available 8am - 8pm'],
    },
    {
      icon: Mail,
      title: 'Email',
      content: ['info@creekbottomranch.com', 'We reply within 24 hours'],
    },
    {
      icon: Clock,
      title: 'Office Hours',
      content: ['Mon-Fri: 8am - 8pm', 'Sat-Sun: 9am - 6pm'],
    },
  ];

  const faqs = [
    {
      question: "What's your cancellation policy?",
      answer:
        'We offer free cancellation up to 48 hours before your check-in date. Cancellations within 48 hours are subject to a one-night fee.',
    },
    {
      question: 'Are pets allowed?',
      answer:
        "Yes! We're a pet-friendly ranch. We allow up to 2 pets per site. Pets must be leashed at all times and owners are responsible for cleanup.",
    },
    {
      question: 'Do you have WiFi?',
      answer:
        'Yes, we provide complimentary high-speed WiFi throughout the ranch. Premium WiFi with higher speeds is available for Premium sites.',
    },
    {
      question: 'What hookups are available?',
      answer:
        'RV sites include both 30 and 50 amp electric with water hookups and a fire ring. Primitive sites are natural tent camping.',
    },
  ];

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      {/* Hero Section */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(5, 8, 20, 0.8), rgba(5, 8, 20, 0.9)), url('https://images.unsplash.com/photo-1625905787174-374f5cd14a9a?auto=format&fit=crop&w=2000&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container mx-auto px-4 z-10 text-center text-[#F4F1EA]">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-[#B8860B]">We're here to help plan your perfect getaway</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]">
              <CardHeader>
                <CardTitle className="text-2xl text-[#F4F1EA]">Send Us a Message</CardTitle>
                <p className="text-[#F4F1EA]/70 mt-2">Have questions? We'd love to hear from you.</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-[#B8860B]">Name *</Label>
                    <Input
                      data-testid="contact-name-input"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={inputClasses}
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-[#B8860B]">Email *</Label>
                      <Input
                        data-testid="contact-email-input"
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={inputClasses}
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#B8860B]">Phone</Label>
                      <Input
                        data-testid="contact-phone-input"
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={inputClasses}
                        placeholder="(832) 714-0101"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-[#B8860B]">Subject *</Label>
                    <Input
                      data-testid="contact-subject-input"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className={inputClasses}
                      placeholder="What is this regarding?"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#B8860B]">Message *</Label>
                    <Textarea
                      data-testid="contact-message-input"
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className={`${inputClasses} min-h-[150px]`}
                      placeholder="Tell us how we can help..."
                    />
                  </div>

                  <Button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA] font-bold py-6 text-lg"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {contactInfo.map((info, index) => (
              <Card
                key={index}
                data-testid={`contact-info-${index}`}
                className="border border-[#B8860B]/30 shadow-lg bg-[#0B132B] hover:shadow-xl transition-shadow duration-300"
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#B8860B] rounded-lg flex items-center justify-center flex-shrink-0 border border-[#B8860B]">
                      <info.icon className="text-[#B8860B]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#B8860B] text-lg mb-2">{info.title}</h3>
                      {info.content.map((line, i) => (
                        <p key={i} className="text-[#F4F1EA]/80 text-sm">
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Card className="border border-[#B8860B]/30 shadow-lg overflow-hidden">
              <div className="aspect-video w-full">
                <iframe
                  data-testid="contact-map-iframe"
                  title="Creek Bottom Ranch — Location"
                  src="https://maps.google.com/maps?q=2724%20Private%20Road%208160%2C%20Lovelady%2C%20TX%2075851&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <div className="p-4 bg-[#050814] border-t border-[#B8860B]/20 text-center">
                <a
                  href="/directions"
                  className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#E5C158] text-sm font-medium"
                >
                  <MapPin size={14} /> Full directions & driving times
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="py-20 bg-[#0B132B] border-t border-[#B8860B]/20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-[#F4F1EA] mb-8 text-center">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card
                  key={idx}
                  data-testid={`faq-card-${idx}`}
                  className="border border-[#B8860B]/30 shadow-lg bg-[#050814]"
                >
                  <CardContent className="p-6">
                    <h3 className="font-bold text-[#B8860B] text-lg mb-2">{faq.question}</h3>
                    <p className="text-[#F4F1EA]/80">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
